// The exam room (RFC-058): a machine's workbench sat for an assigned exam.
// Before Begin, the lobby — the rules, and the machine, the paper and the
// agent checked while no clock runs; after it, the bar with the time left on
// the relay's clock and the hand-ins, the paper's questions down the left,
// and the question shown over its work, a multiple-choice one answered in
// place. Each of these asks the machine (exam/*), whose byj does it.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { language, t, whenAt } from "../i18n";
import { Markdown } from "../markdown";
import { Meter } from "./Meter";
import { CopyButton } from "./Copy";
import { Popover } from "./Popover";
import { agentLabel, clockText, examOver, examWarningOf, parseReceipt, type ExamQuestion, type ExamState, type HandIn } from "../workbench";

const questionLabel = (st: ExamState, q: ExamQuestion) => t("room:questionN", { n: (st.questions ?? []).indexOf(q) + 1 });

/** A question's state as its chip says it. */
export function questionState(q: ExamQuestion): "handed" | "answered" | "open" {
  if (q["handed-in"] > 0) return "handed";
  if (q.mode === "choice" && q.answer && q.answer.length > 0) return "answered";
  return "open";
}

/** A moment as a person keeps to it: its date and time, never "today" or "tomorrow". */
const at = (ms: number) => whenAt(new Date(ms).toISOString());

/**
 * The exam's times before Begin (byj-1wv.51.23): how long it is, that it
 * starts on Begin, when it would end begun now — the earlier of now plus
 * the limit and the window's close, as the relay sets the deadline — and
 * when the window closes; kept current as the minutes pass.
 */
function LobbySchedule({ min, closesAt }: { min?: number | null; closesAt?: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  const closes = closesAt ? Date.parse(closesAt) : NaN;
  const byLimit = min ? now + min * 60_000 : NaN;
  const end = Number.isNaN(byLimit) ? closes : Number.isNaN(closes) ? byLimit : Math.min(byLimit, closes);
  // the times alone, a row each; what they assume said once, under them
  return (
    <div className="room-schedule-box">
      <dl className="room-schedule" aria-label={t("room:schedule")}>
        <div>
          <dt>{t("room:scheduleLength")}</dt>
          <dd>{min ? t("room:lengthMin", { min }) : t("room:noDeadline")}</dd>
        </div>
        <div>
          <dt>{t("room:scheduleStart")}</dt>
          <dd>{at(now)}</dd>
        </div>
        <div>
          <dt>{t("room:scheduleEnd")}</dt>
          <dd>{Number.isNaN(end) ? t("room:endNever") : end === closes && end !== byLimit ? t("room:endAtClose", { at: at(end) }) : at(end)}</dd>
        </div>
        {!Number.isNaN(closes) && (
          <div>
            <dt>{t("room:scheduleClose")}</dt>
            <dd>{at(closes)}</dd>
          </div>
        )}
      </dl>
      <p className="muted small">{t("room:scheduleNote")}</p>
    </div>
  );
}

/** Before Begin: the rules, the checklist, the agent picked, Begin. */
export function ExamLobby({
  st,
  agents,
  agent,
  onAgent,
  machine,
  agentCheck,
  busy,
  onPrepare,
  onCheckAgent,
  onBegin,
  terminal,
  closesAt,
}: {
  st: ExamState;
  /** the agents the paper allows that the machine has */
  agents: string[];
  agent: string;
  onAgent: (name: string) => void;
  machine: boolean;
  /** "ok", a problem in words, or undefined while not checked */
  agentCheck?: string;
  busy?: "prepare" | "check" | "begin";
  onPrepare: () => void;
  onCheckAgent: () => void;
  onBegin: () => void;
  terminal: ReactNode;
  /** when the assignment's window closes, as the relay says it; the sitting ends then at the latest */
  closesAt?: string;
}) {
  const n = st.questions?.length ?? 0;
  const label = agentLabel(st.agent ?? agent);
  const ready = machine && st.prepared && agentCheck === "ok";
  const min = st["time-limit-min"];
  return (
    <div className="room-lobby">
      <section className="room-lobby-text" aria-label={t("room:checklist")}>
        {/* the exam's name is the page head's; this, what the lobby is for */}
        <h2>{t("room:lobbyTitle")}</h2>
        {/* how long the exam is, said where the candidate starts reading */}
        <p className="muted">{min ? t("room:lobbyIntro", { min }) : t("room:lobbyIntroNoLimit")}</p>
        <LobbySchedule min={min} closesAt={closesAt} />
        <h3>{t("room:rules")}</h3>
        <ul className="room-rules">
          {n > 0 && <li>{t("room:ruleQuestions", { count: n })}</li>}
          <li>{t("room:ruleAgent", { agent: label, model: st.model ? t("room:ruleModel", { model: st.model }) : "" })}</li>
          <li>{min ? t("room:ruleTime", { min }) : t("room:ruleNoTime")}</li>
          <li>{(st.attempts ?? 1) > 1 ? t("room:ruleAttempts", { count: st.attempts ?? 1 }) : t("room:ruleAttemptOnce")}</li>
          <li>{t("room:ruleEnd")}</li>
        </ul>
        {/* what a candidate learns too late otherwise: the clock is the relay's,
            a question is its folder, the thread counts, an unsaved edit does
            not, and the room is watched (RFC-058 D1, D2, D8; RFC-057 D8) */}
        <section className="room-notes" aria-labelledby="room-notes-title">
          <h3 id="room-notes-title">{t("room:notes")}</h3>
          <ul>
            <li>{t("room:noteClock")}</li>
            <li>{t("room:noteFolders")}</li>
            <li>{t("room:noteThread", { agent: label })}</li>
            <li>{t("room:noteSave")}</li>
            <li>{t("room:noteWatched")}</li>
          </ul>
        </section>
        <h3>{t("room:checklist")}</h3>
        <ul className="room-checks">
          <li className={machine ? "ok" : ""}>{t("room:checkMachine")}</li>
          <li className={st.prepared ? "ok" : ""}>
            {t("room:checkPaper")}
            {!st.prepared && (
              <span className="room-check-do">
                {agents.length > 1 && (
                  <label className="small">
                    {t("room:pickAgent")}{" "}
                    <select value={agent} disabled={busy !== undefined} onChange={(e) => onAgent(e.target.value)}>
                      {agents.map((a) => (
                        <option key={a} value={a}>
                          {agentLabel(a)}
                        </option>
                      ))}
                    </select>
                  </label>
                )}{" "}
                <button className="small" disabled={!machine || busy !== undefined} onClick={onPrepare}>
                  {busy === "prepare" ? t("room:preparing") : t("room:preparePaper")}
                </button>
              </span>
            )}
          </li>
          <li className={agentCheck === "ok" ? "ok" : ""}>
            {t("room:checkAgent", { agent: label })}
            {agentCheck === "ok" ? (
              <span className="chip chip-live">{t("room:agentOk")}</span>
            ) : (
              <span className="room-check-do">
                <button className="small" disabled={!machine || !st.prepared || busy !== undefined} onClick={onCheckAgent}>
                  {busy === "check" ? t("room:checking") : t("room:checkAgentButton")}
                </button>
              </span>
            )}
          </li>
        </ul>
        {agentCheck && agentCheck !== "ok" && <p className="banner error small">{t("room:agentProblem", { agent: label, m: agentCheck })}</p>}
        <p className="muted small">{t("room:signInHint", { agent: label })}</p>
        <button className="primary" disabled={!ready || busy !== undefined} onClick={onBegin}>
          {busy === "begin" ? t("room:beginning") : t("room:begin")}
        </button>
      </section>
      <div className="room-lobby-terminal">{terminal}</div>
    </div>
  );
}

/** A time of day alone, for an end on its start's day: "4:52 PM", "오후 4:52". */
const timeOnly = (ms: number) => new Date(ms).toLocaleTimeString(language(), { hour: "numeric", minute: "2-digit" });

/** The sitting's span: its start's date and time, its end's time, or date and time when another day. */
function span(start: number, end: number): string {
  const sameDay = new Date(start).toDateString() === new Date(end).toDateString();
  return t("room:timeRange", { start: at(start), end: sameDay ? timeOnly(end) : at(end) });
}

/** When the sitting runs, for the page head's second line: its start's date and time to its end. */
export function examSpan(st: ExamState): string | null {
  const started = st["started-at"];
  return started && st.deadline ? span(started, st.deadline) : null;
}

/**
 * The room's status, in the page's head (byj-1wv.51.23): in one card the
 * time left and the hand-ins as a count and a bar; then how the room works
 * and Hand in all and finish. Where a person looks for how it stands, and
 * no row of its own taken from the work.
 */
export function ExamBar({ st, left, busy, onFinish }: { st: ExamState; left: number | null; busy: boolean; onFinish: () => void }) {
  const qs = st.questions ?? [];
  const handed = qs.filter((q) => q["handed-in"] > 0).length;
  const over = examOver(st, left);
  const warn = examWarningOf(st, left);
  return (
    <div className={`room-bar${warn !== null ? " room-bar-warn" : ""}${over ? " room-bar-over" : ""}`} role="status" aria-label={t("room:questions")}>
      {/* the two that matter, together: the time left and the hand-ins */}
      <div className="room-status">
        <div className="room-stat room-time">
          <span className="room-stat-label">{t("room:timeLeft")}</span>
          <span className="room-clock" aria-live="off">
            {over ? t("room:overShort") : left === null ? t("room:noDeadline") : clockText(left)}
          </span>
        </div>
        <div className="room-stat room-progress">
          <span className="room-stat-label">{t("room:handedInLabel")}</span>
          <span className="room-count">
            {handed}
            <span className="room-count-total">/{qs.length}</span>
          </span>
          {/* in words to a screen reader as the bar's name, not as text copied with the page */}
          <Meter label={t("room:handedIn", { n: handed, total: qs.length })} used={qs.length > 0 ? handed / qs.length : 0} full={false} />
        </div>
      </div>
      <ExamGuide agent={agentLabel(st.agent ?? "byj")} />
      {!over && (
        <button className="small primary" disabled={busy} onClick={onFinish}>
          {busy ? t("room:submitting") : t("room:finish")}
        </button>
      )}
    </div>
  );
}

/**
 * Under the page's head: the end near, or over and handed in for the
 * candidate; and once over or every question is handed in, a way back to
 * the system that sent them, where their exam names one (RFC-061 D5 b).
 */
export function ExamNotice({ st, left, returnURL }: { st: ExamState; left: number | null; returnURL?: string }) {
  const over = examOver(st, left);
  const warn = examWarningOf(st, left);
  const qs = st.questions ?? [];
  const allIn = qs.length > 0 && qs.every((q) => questionState(q) === "handed");
  const back = returnURL && (over || allIn) ? <BackLink url={returnURL} /> : null;
  if (warn !== null && !back) {
    // an end those running the exam moved sooner, which the machine does not
    // know: whether its own hand-in later is taken is the relay's to say,
    // so the last minute promises none (byj-1wv.118.20)
    const said = warn === 1 && st.endedEarly ? t("room:warn1Ended") : t(`room:warn${warn}`);
    return (
      <p className="banner warn small" role="alert">
        {said}
      </p>
    );
  }
  if (over) {
    // ended by those running the exam before the machine's deadline: the
    // machine handed nothing in for the candidate then, and whether what it
    // hands in a minute before its own later deadline is taken is the
    // relay's to say, so nothing is
    // promised either way (byj-1wv.118.19, byj-1wv.118.20)
    const said = st.finished
      ? t("room:finished")
      : st.endedEarly && st.deadline != null
        ? t("room:endedEarly", { at: at(st.deadline) })
        : t("room:over");
    return (
      <p className="banner small">
        {said} {back}
      </p>
    );
  }
  if (back) return <p className="banner small">{t("room:finishNothing")} {back}</p>;
  return null;
}

/** A link back to the portal the candidate came from; none for a URL that is not one. */
function BackLink({ url }: { url: string }) {
  try {
    new URL(url);
  } catch {
    return null;
  }
  return (
    <a className="room-back" href={url}>
      {t("room:backTo")}
    </a>
  );
}

/** How the room is sat, a step a line, opened from the bar whenever it is wanted. */
function ExamGuide({ agent }: { agent: string }) {
  return (
    <Popover label={t("room:guideTitle")} button={t("room:guide")} buttonClassName="small" className="pop" popClassName="pop-panel room-guide" focus="button">
      {() => (
        <div className="stack tight">
          <strong>{t("room:guideTitle")}</strong>
          <ol className="room-guide-steps">
            <li>{t("room:guidePick")}</li>
            <li>{t("room:guideWork", { agent })}</li>
            <li>{t("room:guideChoice")}</li>
            <li>{t("room:guideHandIn")}</li>
            <li>{t("room:guideFinish")}</li>
          </ol>
        </div>
      )}
    </Popover>
  );
}

/**
 * The paper's questions as numbers in a row (byj-1wv.51.23), each coloured
 * by its state — handed in, an answer kept, not handed in — with its name,
 * mode and state in words to a screen reader and on hover: however many
 * there are, they take one line over the question, not the files' column.
 */
export function ExamQuestions({ st, current, onPick }: { st: ExamState; current?: string; onPick: (q: ExamQuestion) => void }) {
  // one line however many: it scrolls sideways, the question shown kept in view
  const row = useRef<HTMLElement>(null);
  useEffect(() => {
    row.current?.querySelector('[aria-current="true"]')?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [current]);
  return (
    <nav className="room-questions" aria-label={t("room:questions")} ref={row}>
      <ol>
        {(st.questions ?? []).map((q, i) => {
          const state = questionState(q);
          const mode = q.mode === "choice" ? t("room:modeChoice") : t("room:modePractical");
          const words = state === "handed" ? t("room:stateHanded") : state === "answered" ? t("room:stateAnswered") : t("room:stateOpen");
          return (
            <li key={q.id}>
              <button
                className={`room-q room-q-${state}`}
                aria-current={q.id === current ? "true" : undefined}
                title={`${questionLabel(st, q)} · ${mode} · ${words}`}
                onClick={() => onPick(q)}
              >
                <span className="room-q-num" aria-hidden="true">
                  {i + 1}
                </span>
                <span className="sr-only">
                  {questionLabel(st, q)} {mode}
                </span>
                <span className="chip sr-only">{words}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * The question shown (byj-1wv.51.23): the paper's numbers over it, then its
 * name, its hand-in and a fold; its text wrapped to the pane and, when long,
 * scrolled in a height of its own rather than pushing the work out of view;
 * a choice's options a row each, the answer marked.
 */
export function ExamQuestionPanel({
  st,
  q,
  busy,
  receipt,
  onAnswer,
  onSubmit,
  nav,
}: {
  st: ExamState;
  q: ExamQuestion;
  busy: boolean;
  /** the question's last hand-in, as the machine told it */
  receipt?: HandIn;
  onAnswer: (answers: number[]) => void;
  onSubmit: () => void;
  /** the paper's questions to move between (ExamQuestions) */
  nav?: ReactNode;
}) {
  const [open, setOpen] = useState(true);
  const left = Math.max(0, (st.attempts ?? 1) - q["handed-in"]);
  const over = st.over === true;
  const choice = q.mode === "choice";
  const chosen = q.answer ?? [];
  const many = (q.choose ?? 1) > 1;
  const pick = (n: number) => {
    if (!many) return onAnswer([n]);
    const next = chosen.includes(n) ? chosen.filter((x) => x !== n) : [...chosen, n];
    if (next.length > 0) onAnswer(next.sort((a, b) => a - b));
  };
  return (
    <section className={`room-question${choice ? " room-question-choice" : ""}`} aria-label={questionLabel(st, q)}>
      {nav}
      <div className="room-question-head">
        {/* the question's line is its fold, a chevron before it as a disclosure's;
            folded, its text's start beside it, so what is folded shows */}
        <button
          className="room-question-fold"
          aria-expanded={open}
          title={open ? t("room:hideQuestion") : t("room:showQuestion")}
          onClick={() => setOpen(!open)}
        >
          <svg className="room-question-chevron" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
            <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <strong>{questionLabel(st, q)}</strong>
          <span className="muted small room-question-kind">{choice ? t("room:modeChoice") : t("room:modePractical")}</span>
          {!open && q.prompt && <span className="room-question-peek">{q.prompt.replace(/[*_`#>[\]()]/g, "").replace(/\s+/g, " ").trim()}</span>}
          <span className="sr-only">{open ? t("room:hideQuestion") : t("room:showQuestion")}</span>
        </button>
        <span className="grow" />
        {/* over, or no hand-in left, nothing is handed in from here: no count, no
            button (the receipt under it says it was handed in) */}
        {!over && left > 0 && (
          <>
            <span className="muted small">{t("room:handInsLeft", { count: left })}</span>
            <button className="small primary" disabled={busy || (choice && chosen.length === 0)} onClick={onSubmit}>
              {busy ? t("room:submitting") : t("room:submitQuestion")}
            </button>
          </>
        )}
        {!over && left === 0 && !receipt && <span className="muted small">{t("room:noHandInsLeft")}</span>}
      </div>
      {open && (
        <div className="room-question-body">
          {q.prompt && <Markdown text={q.prompt} />}
          {choice ? (
            <fieldset className="room-options" disabled={busy || over || left === 0}>
              <legend className="muted small">{many ? t("room:chooseN", { n: q.choose }) : t("room:chooseOne")}</legend>
              {(q.options ?? []).map((o, i) => (
                <label key={i} className={`room-option${chosen.includes(i + 1) ? " room-option-chosen" : ""}`}>
                  <input type={many ? "checkbox" : "radio"} name={`room-${q.id}`} checked={chosen.includes(i + 1)} onChange={() => pick(i + 1)} />
                  <span>{o}</span>
                </label>
              ))}
              <p className="muted small">{t("room:answerKept")}</p>
            </fieldset>
          ) : (
            <p className="muted small">{t("room:workHint")}</p>
          )}
          {receipt && <ReceiptCard q={q} handIn={receipt} attempts={st.attempts ?? 1} />}
        </div>
      )}
    </section>
  );
}

/**
 * A question's last hand-in, in the room's words (byj-1wv.51.23): when, which
 * hand-in and how many are left, what went in — the files or the answer —
 * what was named and not there or not text, the agent's part, and the
 * number to quote to an instructor, copied with a press; what the machine
 * printed under a fold, and alone when it reads as no hand-in.
 */
function ReceiptCard({ q, handIn, attempts }: { q: ExamQuestion; handIn: HandIn; attempts: number }) {
  const r = parseReceipt(handIn.text)?.find((x) => x.question === q.id);
  const left = Math.max(0, attempts - q["handed-in"]);
  return (
    <section className="room-receipt" aria-label={t("room:receipt")}>
      <p className="room-receipt-head">
        <strong>✓ {t("room:receipt")}</strong>
        <span className="muted small">
          {[at(handIn.at), r ? t("room:receiptAttempt", { n: r.n }) : "", left > 0 ? t("room:handInsLeft", { count: left }) : t("room:noHandInsLeft")].filter(Boolean).join(" · ")}
        </span>
        {r?.late && <span className="chip chip-warn">{t("room:receiptLate")}</span>}
      </p>
      {r ? (
        <dl className="room-receipt-rows">
          {r.answered ? (
            <div>
              <dt>{t("room:receiptAnswer")}</dt>
              <dd>{r.answered.map((n) => `${n}. ${q.options?.[n - 1] ?? ""}`.trim()).join(" · ")}</dd>
            </div>
          ) : (
            <div>
              <dt>{t("room:receiptFiles", { count: r.files?.length ?? 0 })}</dt>
              <dd className="room-receipt-files">{r.files && r.files.length > 0 ? r.files.join(" · ") : t("room:receiptNoFiles")}</dd>
            </div>
          )}
          {r.missing && r.missing.length > 0 && (
            <div className="room-receipt-warn">
              <dt>{t("room:receiptMissing")}</dt>
              <dd>{r.missing.join(" · ")}</dd>
            </div>
          )}
          {r.binary && r.binary.length > 0 && (
            <div className="room-receipt-warn">
              <dt>{t("room:receiptBinary")}</dt>
              <dd>{r.binary.join(" · ")}</dd>
            </div>
          )}
          {r.sessions !== undefined && r.toolCalls !== undefined && (
            <div>
              <dt>{t("room:receiptAgent")}</dt>
              <dd>{t("room:receiptAgentUse", { sessions: r.sessions, calls: r.toolCalls })}</dd>
            </div>
          )}
          {r.hash && (
            <div>
              <dt>{t("room:receiptId")}</dt>
              <dd className="room-receipt-id">
                <code>{r.hash.slice(0, 8)}</code> <CopyButton text={r.hash} icon label={t("room:receiptIdCopy")} />
              </dd>
            </div>
          )}
        </dl>
      ) : null}
      <details className="room-receipt-raw" open={r ? undefined : true}>
        <summary className="small muted">{t("room:receiptRaw")}</summary>
        <pre>{handIn.text}</pre>
      </details>
    </section>
  );
}
